/**
 * Fiscal Period Service
 *
 * API client for fiscal year and period management.
 */

import { get, post, buildQueryString, type ApiResponse, type PageInfo } from './api-client';
import {
  PERIOD_TYPE_LABELS,
  PERIOD_STATUS_LABELS,
  type PeriodType,
  type PeriodStatus,
} from '../constants/labels';
import { buildPaginationParams } from '../utils/api-helpers';
import { getLabel } from '../utils/labels';

import type { Money, AuditInfo } from '../types/common';

// Re-export types for backward compatibility
export type { PeriodType, PeriodStatus } from '../constants/labels';

// ============================================================================
// Types
// ============================================================================

export interface FiscalPeriod {
  id: string;
  orgId: string;
  name: string;
  periodType: PeriodType;
  fiscalYear: number;
  periodNumber: number;
  startDate: string;
  endDate: string;
  status: PeriodStatus;
  closedAt?: string;
  closedBy?: string;
  lockedAt?: string;
  lockedBy?: string;
  audit: AuditInfo;
}

export interface CreateFiscalYearRequest {
  fiscalYear: number;
  startDate: string;
  createMonthlyPeriods?: boolean;
  createQuarterlyPeriods?: boolean;
  idempotencyKey: string;
}

export interface CreateFiscalYearResponse {
  periods: FiscalPeriod[];
}

export interface ListFiscalPeriodsFilter {
  fiscalYear?: number;
  periodType?: PeriodType;
  status?: PeriodStatus;
}

export interface ListFiscalPeriodsResponse {
  periods: FiscalPeriod[];
  pageInfo: PageInfo;
}

export interface CloseFiscalPeriodRequest {
  generateClosingEntries?: boolean;
  retainedEarningsAccountId?: string;
  idempotencyKey: string;
}

export interface PeriodCloseWarning {
  code: string;
  message: string;
  accountId?: string;
}

export interface CloseFiscalPeriodResponse {
  period: FiscalPeriod;
  closingEntryId?: string;
  warnings: PeriodCloseWarning[];
}

export interface ReopenFiscalPeriodRequest {
  reason: string;
}

export interface LockFiscalPeriodRequest {
  reason?: string;
}

export interface GenerateClosingEntriesRequest {
  fiscalYear: number;
  retainedEarningsAccountId: string;
  closingDate: string;
  previewOnly?: boolean;
  idempotencyKey: string;
}

export interface ClosingEntryPreview {
  accountId: string;
  accountName: string;
  amount: Money;
  isDebit: boolean;
}

export interface GenerateClosingEntriesResponse {
  revenueClosingEntryId?: string;
  totalRevenue: Money;
  expenseClosingEntryId?: string;
  totalExpenses: Money;
  netIncome: Money;
  isProfit: boolean;
  previewEntries?: ClosingEntryPreview[];
}

// ============================================================================
// API Functions
// ============================================================================

/**
 * Create a new fiscal year with periods.
 */
export async function createFiscalYear(
  orgId: string,
  data: CreateFiscalYearRequest
): Promise<ApiResponse<CreateFiscalYearResponse>> {
  return post<CreateFiscalYearResponse>(`/organizations/${orgId}/fiscal-years`, data);
}

/**
 * Get fiscal period by ID.
 */
export async function getFiscalPeriod(
  orgId: string,
  periodId: string
): Promise<ApiResponse<FiscalPeriod>> {
  return get<FiscalPeriod>(`/organizations/${orgId}/fiscal-periods/${periodId}`);
}

/**
 * List fiscal periods.
 */
export async function listFiscalPeriods(
  orgId: string,
  filter?: ListFiscalPeriodsFilter,
  pageSize?: number,
  pageToken?: string
): Promise<ApiResponse<ListFiscalPeriodsResponse>> {
  const query = buildQueryString({
    ...buildPaginationParams(pageSize, pageToken),
    fiscalYear: filter?.fiscalYear,
    periodType: filter?.periodType,
    status: filter?.status,
  });
  return get<ListFiscalPeriodsResponse>(`/organizations/${orgId}/fiscal-periods${query}`);
}

/**
 * Get the current fiscal period.
 * If no period type is specified, tries MONTH → QUARTER → YEAR in order.
 */
export async function getCurrentPeriod(
  orgId: string,
  periodType?: PeriodType
): Promise<ApiResponse<FiscalPeriod>> {
  // If period type is specified, use it directly
  if (periodType) {
    const query = buildQueryString({ periodType });
    return get<FiscalPeriod>(`/organizations/${orgId}/fiscal-periods/current${query}`);
  }

  // Otherwise, try period types in order of granularity: MONTH → QUARTER → YEAR
  const typesToTry: PeriodType[] = ['PERIOD_TYPE_MONTH', 'PERIOD_TYPE_QUARTER', 'PERIOD_TYPE_YEAR'];

  for (const type of typesToTry) {
    const query = buildQueryString({ periodType: type });
    const response = await get<FiscalPeriod>(`/organizations/${orgId}/fiscal-periods/current${query}`);

    // If successful, return the result
    if (response.data) {
      return response;
    }

    // If it's a 404 or "not found" error, try the next type
    // Error code could be '5' (gRPC NOT_FOUND), 'HTTP_404', or '404'
    const errorCode = response.error?.code?.toString();

    if (errorCode === '5' || errorCode === '404' || errorCode === 'HTTP_404' || errorCode?.includes('404')) {
      continue;
    }

    // For other errors, return the error immediately
    return response;
  }

  // If all types failed, return a not found error
  return {
    error: {
      message: 'No fiscal periods found for any period type',
      code: '404'
    }
  };
}

/**
 * Get the fiscal period containing a specific date.
 */
export async function getPeriodForDate(
  orgId: string,
  date: string,
  periodType?: PeriodType
): Promise<ApiResponse<FiscalPeriod>> {
  const query = buildQueryString({ date, periodType });
  return get<FiscalPeriod>(`/organizations/${orgId}/fiscal-periods/for-date${query}`);
}

/**
 * Close a fiscal period.
 */
export async function closeFiscalPeriod(
  orgId: string,
  periodId: string,
  data: CloseFiscalPeriodRequest
): Promise<ApiResponse<CloseFiscalPeriodResponse>> {
  return post<CloseFiscalPeriodResponse>(
    `/organizations/${orgId}/fiscal-periods/${periodId}:close`,
    data
  );
}

/**
 * Reopen a closed fiscal period.
 */
export async function reopenFiscalPeriod(
  orgId: string,
  periodId: string,
  data: ReopenFiscalPeriodRequest
): Promise<ApiResponse<FiscalPeriod>> {
  return post<FiscalPeriod>(
    `/organizations/${orgId}/fiscal-periods/${periodId}:reopen`,
    data
  );
}

/**
 * Lock a fiscal period permanently.
 */
export async function lockFiscalPeriod(
  orgId: string,
  periodId: string,
  data?: LockFiscalPeriodRequest
): Promise<ApiResponse<FiscalPeriod>> {
  return post<FiscalPeriod>(
    `/organizations/${orgId}/fiscal-periods/${periodId}:lock`,
    data ?? {}
  );
}

/**
 * Generate year-end closing entries.
 */
export async function generateClosingEntries(
  orgId: string,
  data: GenerateClosingEntriesRequest
): Promise<ApiResponse<GenerateClosingEntriesResponse>> {
  return post<GenerateClosingEntriesResponse>(
    `/organizations/${orgId}/fiscal-years/${data.fiscalYear}/closing-entries`,
    data
  );
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Get the display name for a period type.
 */
export function getPeriodTypeLabel(type: PeriodType): string {
  return getLabel(type, PERIOD_TYPE_LABELS);
}

/**
 * Check if an organization has any fiscal periods configured.
 * Returns true if at least one period exists, false otherwise.
 */
export async function hasAnyFiscalPeriods(orgId: string): Promise<boolean> {
  try {
    const response = await listFiscalPeriods(orgId, undefined, 1);

    // Check if the API call was successful
    if (response.error ?? !response.data) {
      return false;
    }

    return response.data.periods.length > 0;
  } catch (_error) {
    return false;
  }
}

/**
 * Check if there is an open fiscal period covering today.
 * Returns true if a current open period exists, false if no period covers today.
 */
export async function hasOpenCurrentPeriod(orgId: string): Promise<boolean> {
  try {
    const response = await getCurrentPeriod(orgId);
    if (!response.data) {return false;}
    return response.data.status === 'PERIOD_STATUS_OPEN';
  } catch {
    return false;
  }
}

/**
 * Get the display name for a period status.
 */
export function getPeriodStatusLabel(status: PeriodStatus): string {
  return getLabel(status, PERIOD_STATUS_LABELS);
}

/**
 * Check if a period can be closed.
 */
export function canClosePeriod(period: FiscalPeriod): boolean {
  return period.status === 'PERIOD_STATUS_OPEN';
}

/**
 * Check if a period can be reopened.
 */
export function canReopenPeriod(period: FiscalPeriod): boolean {
  return period.status === 'PERIOD_STATUS_CLOSED';
}

/**
 * Check if a period can be locked.
 */
export function canLockPeriod(period: FiscalPeriod): boolean {
  return period.status === 'PERIOD_STATUS_CLOSED';
}

/**
 * Format period date range.
 */
export function formatPeriodDateRange(period: FiscalPeriod): string {
  return `${period.startDate} to ${period.endDate}`;
}
